import { Head, Link } from "@inertiajs/react"

import {
  RiderStatusBadge,
  WorkOrderTypeChip,
} from "@/components/products/fleet/badges"
import {
  EMPTY,
  formatDate,
  formatDays,
  formatNumber,
  humanize,
} from "@/components/products/fleet/format"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { formatMoney } from "@/lib/money-format"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface Contact {
  name?: string
  email?: string
  phone?: string
  role?: string
}

interface RiderRow {
  id: number
  rider_number: string
  effective_on: string | null
  expiration_on: string | null
  monthly_rate_cents: number | null
  status: string
  expiring_soon: boolean
  car_count: number | null
  cars: number
}

interface WaitingRow {
  id: number
  type: string
  status: string
  car_id: number | null
  car: string | null
  facility: string | null
  defect_category: string | null
  waiting_since: string | null
  days_waiting: number | null
  last_followed_up_at: string | null
}

interface ScorecardMetric {
  id: number
  category: string
  metric_key: string
  value: number | null
  target: number | null
  unit: string | null
}

interface Props {
  lessor: {
    id: number
    name: string
    active: boolean
    contacts: Contact[]
    notes: string | null
    cars: number
  }
  riders: RiderRow[]
  open_work_orders: WaitingRow[]
  scorecard: {
    id: number
    period: string
    generated_at: string | null
    notes: string | null
    metrics: ScorecardMetric[]
  } | null
}

export default function LessorShow({
  lessor,
  riders,
  open_work_orders,
  scorecard,
}: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Lessors", href: routes.lessorsPath() },
    { title: lessor.name, href: routes.lessorPath(lessor.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={lessor.name} />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">
                {lessor.name}
              </h1>
              {!lessor.active && <Badge variant="secondary">Inactive</Badge>}
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {formatNumber(lessor.cars)} cars · {riders.length}{" "}
              {riders.length === 1 ? "rider" : "riders"}
            </p>
          </div>
          {lessor.contacts.length > 0 && (
            <div className="text-sm">
              {lessor.contacts.map((contact, index) => (
                <div key={index} className="text-right">
                  <span className="font-medium">
                    {contact.name ?? contact.role ?? "Contact"}
                  </span>
                  {contact.email && (
                    <span className="text-muted-foreground">
                      {" "}
                      · {contact.email}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Riders</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Rider</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Effective</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Rate / mo</TableHead>
                  <TableHead className="pr-6 text-right">Cars</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {riders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground py-8 pl-6 text-sm"
                    >
                      No riders on file for this lessor yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  riders.map((rider) => (
                    <TableRow key={rider.id}>
                      <TableCell className="pl-6 font-medium">
                        {rider.rider_number}
                      </TableCell>
                      <TableCell>
                        <RiderStatusBadge
                          status={rider.status}
                          expiringSoon={rider.expiring_soon}
                        />
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(rider.effective_on)}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-sm",
                          rider.expiring_soon &&
                            "font-medium text-amber-600 dark:text-amber-400",
                        )}
                      >
                        {formatDate(rider.expiration_on)}
                      </TableCell>
                      <TableCell className="text-right text-sm tabular-nums">
                        {formatMoney(rider.monthly_rate_cents)}
                      </TableCell>
                      <TableCell className="pr-6 text-right text-sm tabular-nums">
                        {rider.cars}
                        {rider.car_count != null &&
                          rider.car_count !== rider.cars && (
                            <span className="text-muted-foreground">
                              {" "}
                              / {rider.car_count} on contract
                            </span>
                          )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Waiting on {lessor.name}</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">Car</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Stage</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead className="text-right">Days waiting</TableHead>
                  <TableHead className="pr-6">Last follow-up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {open_work_orders.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-muted-foreground py-8 pl-6 text-sm"
                    >
                      Nothing is waiting on this lessor. Every open work order
                      on their cars is with us, a shop, or the FRA.
                    </TableCell>
                  </TableRow>
                ) : (
                  open_work_orders.map((wo) => (
                    <TableRow key={wo.id}>
                      <TableCell className="pl-6 font-medium">
                        {wo.car_id ? (
                          <Link
                            href={routes.carPath(wo.car_id)}
                            className="hover:underline"
                          >
                            {wo.car ?? EMPTY}
                          </Link>
                        ) : (
                          (wo.car ?? EMPTY)
                        )}
                      </TableCell>
                      <TableCell>
                        <Link href={routes.workOrderPath(wo.id)}>
                          <WorkOrderTypeChip type={wo.type} />
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm">
                        {humanize(wo.status)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {wo.facility ?? EMPTY}
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium tabular-nums">
                        {formatDays(wo.days_waiting)}
                      </TableCell>
                      <TableCell className="text-muted-foreground pr-6 text-sm">
                        {formatDate(wo.last_followed_up_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <ScorecardTile scorecard={scorecard} lessorName={lessor.name} />
      </div>
    </AppLayout>
  )
}

function ScorecardTile({
  scorecard,
  lessorName,
}: {
  scorecard: Props["scorecard"]
  lessorName: string
}) {
  if (!scorecard) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Scorecard</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
            No scorecard has been built for {lessorName} yet. Quarterly grids
            appear here once there is enough closed history to grade.
          </p>
        </CardContent>
      </Card>
    )
  }

  const byCategory = new Map<string, ScorecardMetric[]>()
  for (const metric of scorecard.metrics) {
    const bucket = byCategory.get(metric.category)
    if (bucket) bucket.push(metric)
    else byCategory.set(metric.category, [metric])
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scorecard · {scorecard.period}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {[...byCategory.entries()].map(([category, metrics]) => (
          <div key={category}>
            <h3 className="text-muted-foreground mb-2 text-xs font-medium tracking-wide uppercase">
              {category}
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {metrics.map((metric) => (
                <MetricTile key={metric.id} metric={metric} />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function MetricTile({ metric }: { metric: ScorecardMetric }) {
  // A metric with no target is reported, not graded — there is nothing to be
  // off by, so it stays neutral rather than being scored against zero.
  const missed =
    metric.target != null &&
    metric.value != null &&
    metric.value > metric.target

  return (
    <div className="rounded-md border p-3">
      <p className="text-muted-foreground text-xs">
        {humanize(metric.metric_key)}
      </p>
      <p
        className={cn(
          "mt-1 text-lg font-semibold tabular-nums",
          missed && "text-red-600 dark:text-red-400",
        )}
      >
        {metric.value != null ? metric.value.toLocaleString() : EMPTY}
        {metric.unit && (
          <span className="text-muted-foreground ml-1 text-xs font-normal">
            {metric.unit}
          </span>
        )}
      </p>
      {metric.target != null && (
        <p className="text-muted-foreground mt-0.5 text-xs tabular-nums">
          target {metric.target.toLocaleString()}
        </p>
      )}
    </div>
  )
}
